import { IObjectTranslatable, RelationalActionTranslator, TextLineConverter, TextAlignKindsTranslator } from ".";
import { ITextBoxItemBean, ITypeDefinable, BeanTypes } from "../beans/items";
import {
  IItem,
  TextBoxItem,
  NumberingInformation,
} from "../../../../domain/document/items";

import {
  Point,
  Size,
  Rectangle,
  Color,
  TextStyle,
} from "../../../../domain/drawing";
import { HtmlColorCodeTranslator } from "./HtmlColorCodeTranslator";
import { HisItemParameterTranslator } from "../../../his/ver2/HisItemParameterTranslator";
import { RelationalActionExtractor } from "./RelationalActionExtractor";
import { TextBoxInputMethodKinds } from "../../../../domain/document/inputMethodKinds";
import { TextBoxInputMethodOptionKinds } from "../../../../domain/document/inputMethodOptionKinds";
import { NumberingKinds } from "../../../../domain/document";

export class TextBoxItemTranslator implements IObjectTranslatable {
  toItem(item: ITypeDefinable, formatId: number, groupId: number, documentId: number, executionMode: string): IItem {
    const bean = item as ITextBoxItemBean;
    const result = new TextBoxItem(TextLineConverter.convert(bean.text, !!bean.multiLine, !bean.supportedFreeText));
    result.privateId = (bean as any).privateId; // フロントエンド独自パラメータ.
    result.uniqueKey = bean.uniqueKey;
    result.id = bean.id;
    result.sectionObjectUid = bean.sectionObjectUid;
    result.isEditable = true; // TODO:特に問題なければプロパティを削除する
    result.isAdditional = !!bean.isAdditional;
    result.isDeleted = !!bean.isDeleted;
    result.isVisible = !!bean.isVisible;
    result.typeDetail = bean.typeDetail;
    result.viewTitle = bean.viewTitle;
    result.printBehavior.shouldPrint = !!bean.printVisible;
    result.printBehavior.shouldPreview = !!bean.previewVisible;
    result.name = bean.name;
    result.enableNameGroupEmptyCheck = bean.nameGroupEmptyCheck;
    result.subName = bean.subName;
    result.groupKey = bean.groupKey;
    result.actions = RelationalActionTranslator.toItems(bean.relationalItems);
    result.shouldQuote = !!bean.isQuotable;
    result.supportedQuotable = !!bean.supportedQuotable;
    const adjustedHeight = bean.adjustedHeight ? bean.adjustedHeight : bean.height;
    result.geometry.rectangle = new Rectangle(new Point(bean.x, bean.y), new Size(bean.width, adjustedHeight));
    result.textStyle = new TextStyle(new Color(bean.textColor), TextAlignKindsTranslator.toKind(bean.textAlign), bean.textFontSize, bean.adjustedTextFontSize, bean.textFontName, bean.textLineHeight, !!bean.multiLine);
    result.imeMode = bean.defaultIME;
    result.isFreeInputable = bean.supportedFreeText;
    result.inputMethodOptionKind = TextBoxInputMethodOptionKinds.toInputMethodOptionKinds(bean);
    result.inputMethodKind = this.toInputMethodKind(bean, result.inputMethodOptionKind);
    result.numericFormat = bean.numericDispType;
    result.enableSingleEmptyCheck = bean.emptyCheck;
    result.enableEmptyCheckConfirmation = bean.isEmptyCheckIgnorable;
    result.alertGroupKey = bean.alertGroup;
    result.userDefinitionKey = bean.sourceFile;
    result.userDefinitionSelectionMode = bean.selectionMode;
    result.itemName = bean.itemName;
    result.userDefinitionWindowKind = bean.windowType;
    result.isHisInfo = !!bean.isHisInfo;
    result.displayName = bean.displayName;
    result.isNumeric = !!bean.isNumeric;
    result.groupName = bean.groupName;
    result.enabledAutoAdjustFontSize = bean.enabledAutoAdjustFontSize;
    result.isNote = !!bean.isNote;
    result.relatedObjectKey = bean.extraPageSourceObjectKey;
    result.relatedObjectId = bean.extraPageSourceObjectId;
    result.relatedObjectName = bean.extraPageSourceObjectName;
    result.relationObjectKey = bean.extraPageTargetObjectKey;
    result.relationObjectId = bean.extraPageTargetObjectId;
    result.relationObjectName = bean.extraPageTargetObjectName;
    result.toolTip = bean.toolTip;
    result.dataValidationKind = bean.dataValidation;
    result.autoNotePageSize = bean.autoNotePageSize;
    result.autoNotePageKind = bean.autoNotePageType;
    result.separatorKind = bean.separatorType;
    result.hisItemParameter = HisItemParameterTranslator.toHisItemParameter(bean.hisItemParameter);
    result.scriptExecuteParameters = RelationalActionExtractor.extractScriptExecuteParameters(result.actions);
    result.isDialogMultiSelectable = !!bean.enabledHisMultiSelect;
    result.designatedDoctorNumberType = RelationalActionExtractor.extractDesignatedDoctorNumberType(result.actions);
    result.asteriskKind = bean.asteriskType;
    result.enableLengthCheck = !!bean.lengthCheck;
    result.lengthChecklength = bean.length;
    result.numericAlertKind = bean.overType;
    result.upperLimit = bean.highValue;
    result.lowerLimit = bean.lowValue;
    result.upperLimitAlertColor = bean.highOverColor;
    result.lowerLimitAlertColor = bean.lowOverColor;
    result.pasteDestinations = RelationalActionExtractor.extractTextPasteParameters(result.actions);
    result.parentAlertInformations = RelationalActionExtractor.extractParentAlertParameters(result.actions);
    result.isInitial = bean.isInitial;
    result.initialSettingKind = bean.initialSetting;
    result.shouldSelectHospitalizationInitialValueHospitalizedOnly = !!bean.initialAdmission;
    result.mksTemplateFormats = RelationalActionExtractor.extractMksTemplateFormats(result.actions);
    result.mksTemplateRegisteredId = bean.mksTemplateRegisteredId;
    result.dynamicTemplateFormats = RelationalActionExtractor.extractDynamicTemplateFormats(result.actions);
    result.dynamicTemplateRegisteredId = bean.dynamicTemplateRegisteredId;
    result.paintStyle = HtmlColorCodeTranslator.toPaintStyleFill(bean.backColor);
    result.paintStyleOnEditing = bean.isDisplayBackColorSpecified
      ? HtmlColorCodeTranslator.toPaintStyleFill(bean.displayBackColor)
      : null;
    result.originalHeight = bean.height;
    result.isFocusable = !!bean.focus;
    result.isRelayoutableByHeight = bean.isChangeHeight;
    result.numberingInformation = this.toNumberingInformation(bean);
    result.numberingIdentifier = (bean.numberingIdentifier != null) ? bean.numberingIdentifier : "";
    result.originalNumberingIdentifier = result.numberingIdentifier;
    result.numberingIdentifierSources = RelationalActionExtractor.extractNumberingIdentifierSourceParameters(result.actions);
    result.linkedValue = bean.linkedValue;
    result.userDefineLinkActions = RelationalActionExtractor.extractUserDefineLinkParameters(result.actions);
    result.userDefineSearchActions = RelationalActionExtractor.extractUserDefineSearchParameters(result.actions);
    result.isReadonlyFreeInput = !!bean.isReadonlyFreeInput;
    result.dacsProcessType = bean.dacsProcessType;
    result.dacsItemName = bean.dacsItemName;
    result.dacsItemCode = bean.dacsItemCode;
    result.isNaturalViewEnable = bean.naturalView;
    result.naturalText = bean.naturalText;
    result.clickChainedObjectPrivateId = null;
    return result;
  }

  /**
   * 採番にまつわる細かいパラメータはバックエンドでの処理時に使用する
   * 基本的にフロントエンドは貰ったものをそのまま返却する
   */
  toNumberingInformation(bean: ITextBoxItemBean): NumberingInformation {
    const info = new NumberingInformation();
    info.numberingValue = bean.numberingValue;
    info.numberingKind = bean.numberingType;
    info.numberingPaddingKind = bean.numberingPaddingType;
    info.numberingPaddingLength = bean.numberingPaddingLength;
    info.numberingResetType = bean.numberingResetType;
    info.numberingFirstNumber = bean.numberingFirstNumber;
    info.numberingPrefix = bean.numberingPrefix;
    return info;
  }

  isFixLabel(bean: ITextBoxItemBean): boolean {
    if (bean.isHisInfo) { return false; }
    return !bean.supportedFreeText;
  }

  private toInputMethodKind(bean: ITextBoxItemBean, optionKind: TextBoxInputMethodOptionKinds): TextBoxInputMethodKinds {
    // MKS・DYNAMICテンプレートの指定になっていても、古い実装で移行されたデータではenabledFreeTextがtrueになってしまっているので対策している
    if (bean.enabledFreeText) {
      if (optionKind === TextBoxInputMethodOptionKinds.MKSTemplate && (bean.mksTemplateRegisteredId || !bean.text)) {
        // 救済必要なデータなのでMKSテンプレート入力にする
        return TextBoxInputMethodKinds.MKSTemplate;
      }
      if (optionKind === TextBoxInputMethodOptionKinds.DynamicTemplate && (bean.dynamicTemplateRegisteredId || !bean.text)) {
        // 救済必要なデータなのでDynamicテンプレート入力にする
        return TextBoxInputMethodKinds.DynamicTemplate;
      }
      return TextBoxInputMethodKinds.FreeInput;
    }
    return TextBoxInputMethodOptionKinds.getDefualtInputMethodKind(optionKind);
  }

  toBean(item: IItem): ITypeDefinable {
    const target = item as TextBoxItem;
    // 採番オブジェクトで採番記号が変更されたとき、採番記号（全体）が指定されているオブジェクトだけ再採番を行う
    const isNumberingIdentifierUpdated = target.numberingIdentifier !== target.originalNumberingIdentifier;
    const shouldResetNumberingValue = isNumberingIdentifierUpdated && target.numberingInformation && target.numberingInformation.numberingKind === NumberingKinds.Keyword;
    const bean: ITextBoxItemBean = {
      type: BeanTypes.Text,
      uniqueKey: target.uniqueKey,
      id: target.id,
      sectionObjectUid: target.sectionObjectUid,
      isVisible: target.isVisible,
      printVisible: target.printBehavior.shouldPrint,
      previewVisible: target.printBehavior.shouldPreview,
      x: target.geometry.rectangle.x,
      y: target.geometry.rectangle.y,
      width: target.geometry.rectangle.width,
      adjustedHeight: target.geometry.rectangle.height,
      height: target.originalHeight,
      isAdditional: target.isAdditional,
      isDeleted: target.isDeleted,
      name: target.name,
      nameGroupEmptyCheck: target.enableNameGroupEmptyCheck,
      subName: target.subName,
      groupKey: target.groupKey,
      textColor: HtmlColorCodeTranslator.fromColor(target.textStyle.color),
      textFontName: target.textStyle.fontFamily,
      textFontSize: target.textStyle.baseFontSize,
      textLineHeight: target.textStyle.lineHeight,
      textAlign: TextAlignKindsTranslator.fromKind(target.textStyle.textAlign),
      backColor: HtmlColorCodeTranslator.fromPaintStyle(target.paintStyle),
      displayBackColor: HtmlColorCodeTranslator.fromPaintStyle(target.paintStyleOnEditing),
      defaultIME: target.imeMode,
      supportedFreeText: target.isFreeInputable,
      enabledFreeText: target.shouldInputFree,
      numericDispType: target.numericFormat,
      text: target.value,
      relationalItems: null,  // TODO:返却する必要ある？
      emptyCheck: target.enableSingleEmptyCheck,
      isEmptyCheckIgnorable: target.enableEmptyCheckConfirmation,
      alertGroup: target.alertGroupKey,
      sourceFile: target.userDefinitionKey,
      selectionMode: target.userDefinitionSelectionMode,
      itemName: target.itemName,
      windowType: target.userDefinitionWindowKind,
      isHisInfo: target.isHisInfo,
      displayName: target.displayName,
      isNumeric: target.isNumeric,
      groupName: target.groupName,
      enabledAutoAdjustFontSize: target.enabledAutoAdjustFontSize,
      isNote: target.isNote,
      extraPageSourceObjectKey: target.relatedObjectKey,
      extraPageSourceObjectId: target.relatedObjectId,
      extraPageSourceObjectName: target.relatedObjectName,
      extraPageTargetObjectKey: target.relationObjectKey,
      extraPageTargetObjectId: target.relationObjectId,
      extraPageTargetObjectName: target.relationObjectName,
      toolTip: target.toolTip,
      dataValidation: target.dataValidationKind,
      autoNotePageSize: target.autoNotePageSize,
      autoNotePageType: target.autoNotePageKind,
      isQuotable: target.shouldQuote,
      supportedQuotable: target.supportedQuotable,
      separatorType: target.separatorKind,
      hisItemParameter: null, // 返却する必要なし
      enabledHisMultiSelect: target.isDialogMultiSelectable,
      asteriskType: target.asteriskKind,
      lengthCheck: target.enableLengthCheck,
      length: target.lengthChecklength,
      overType: target.numericAlertKind,
      highValue: target.upperLimit,
      lowValue: target.lowerLimit,
      highOverColor: target.upperLimitAlertColor,
      lowOverColor: target.lowerLimitAlertColor,
      adjustedTextFontSize: target.textStyle.adjustedFontSize,
      isInitial: target.isInitial,
      initialSetting: target.initialSettingKind,
      initialAdmission: target.shouldSelectHospitalizationInitialValueHospitalizedOnly,
      multiLine: target.textStyle.isMultiLine,
      mksTemplateRegisteredId: target.mksTemplateRegisteredId,
      dynamicTemplateRegisteredId: target.dynamicTemplateRegisteredId,
      isDisplayBackColorSpecified: !!target.paintStyleOnEditing,
      focus: target.isFocusable,
      isChangeHeight: target.isRelayoutableByHeight,
      numberingValue: shouldResetNumberingValue ? undefined : (target.numberingInformation ? target.numberingInformation.numberingValue : undefined),
      numberingType: target.numberingInformation ? target.numberingInformation.numberingKind : undefined,
      numberingPaddingType: target.numberingInformation ? target.numberingInformation.numberingPaddingKind : undefined,
      numberingPaddingLength: target.numberingInformation ? target.numberingInformation.numberingPaddingLength : undefined,
      numberingResetType: target.numberingInformation ? target.numberingInformation.numberingResetType : undefined,
      numberingFirstNumber: target.numberingInformation ? target.numberingInformation.numberingFirstNumber : undefined,
      numberingPrefix: target.numberingInformation ? target.numberingInformation.numberingPrefix : undefined,
      numberingIdentifier: target.numberingIdentifier,
      linkedValue: target.linkedValue,
      isReadonlyFreeInput: target.isReadonlyFreeInput,
      typeDetail: target.typeDetail,
      viewTitle: target.viewTitle,
      dacsProcessType: target.dacsProcessType,
      dacsItemName: target.dacsItemName,
      dacsItemCode: target.dacsItemCode,
      naturalView: target.isNaturalViewEnable,
      naturalText: target.naturalText,
    };
    return bean;
  }
}
